import { useCallback, useEffect, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import Script from "next/script";
import classNames from "classnames";
import useSWR from "swr";
import times from "lodash/times";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";
import once from "lodash/once";

import { getStaticProps, StoryProvider } from "../[...slug]";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Link from "../../components/Link";
import CentraProductImage from "../../components/CentraProductImage";
import { centraGet, centraPut, centraPost } from "../../lib/centra-api";
import {
  numberWithSpaces,
  useBodyClass,
  useCentraToken,
} from "../../lib/utils";
import * as ga from "../../lib/ga";

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    _ingrid_initializeWidgetInstance: any;
    CentraCheckout: any;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

type AddressField = {
  name: string;
  label: string;
  width: string;
  required?: boolean;
  type?: string;
};

type VoucherFormStatus = "" | "submitting" | "error" | "notfound" | "success";

// Inserting <script> elements by setting innerHTML doesn't execute inline
// scripts. This function employs a neat hack to get around that. Taken from
// https://stackoverflow.com/a/47614491/618739
function enlivenScripts(element: HTMLElement) {
  const scripts = element.querySelectorAll("script");

  for (const oldScript of Array.from(scripts)) {
    const script = document.createElement("script");

    for (const attr of Array.from(oldScript.attributes)) {
      script.setAttribute(attr.name, attr.value);
    }

    script.appendChild(document.createTextNode(oldScript.innerHTML));
    oldScript.parentNode.replaceChild(script, oldScript);
  }
}

function leftPad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

export default function Checkout(props) {
  const [token] = useCentraToken();

  const { data, mutate: mutateSelection } = useSWR(
    "https://monokel.centra.com/api/checkout/selection",
    (uri) => centraGet(uri, token)
  );
  const ingridRef = useRef<HTMLDivElement>();
  const adyenRef = useRef<HTMLDivElement>();

  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [voucherFormStatus, setVoucherFormStatus] =
    useState<VoucherFormStatus>("");
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<
    "address" | "shipping" | "payment"
  >("shipping");
  const [showConfirmDetailsModal, setShowConfirmDetailsModal] = useState(false);
  const [currentAdressDetails, setCurrentAdressDetails] = useState<any>(); //Only used for displying the data under confirm.
  const [activeInput, setActiveInput] = useState("");
  const [differentBillingAddress, setDifferentBillingAddress] = useState(false);
  const [billingCountry, setBillingCountry] = useState(data?.location.country);
  const [isEU, setIsEU] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const [adyenHtml, setAdyenHtml] = useState("");

  useBodyClass("is-checkout");

  useEffect(() => {
    setIsEU(data?.location.eu);
    console.log(data?.selection);
  }, [data]);

  async function setItemQuantity(item, quantity) {
    window.CentraCheckout?.suspend();
    setIsAdjusting(true);

    const res = await fetch(
      `https://monokel.centra.com/api/checkout/lines/${item.line}`,
      {
        method: quantity > item.quantity ? "POST" : "DELETE",
        headers: {
          accept: `*/*; api-token=${token || "none"}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          quantity: Math.abs(quantity - item.quantity),
        }),
      }
    );

    const data = await res.json();
    mutateSelection(data, false);
    window.CentraCheckout?.resume();
    setIsAdjusting(false);
  }

  async function setCustomerCountry(country: string) {
    window.CentraCheckout?.suspend();
    setIsAdjusting(true);
    const data = await centraPut(
      `https://monokel.centra.com/api/checkout/countries/${country}`,
      token
    );
    setIsEU(data.location.eu);
    mutateSelection(data, false);
    window.CentraCheckout?.resume();
    setIsAdjusting(false);
  }

  async function changeBillingCountry(country: string) {
    window.CentraCheckout?.suspend();
    setIsAdjusting(true);

    const data = await centraPut(
      `https://monokel.centra.com/api/checkout/countries/${country}`,
      token
    );
    window.CentraCheckout?.resume();
    setIsAdjusting(false);
  }

  const submitApplyVoucher = useCallback(async (event) => {
    setVoucherFormStatus("submitting");
    const data = new FormData(event.target);
    const voucher = data?.get("voucher");

    try {
      const data = await centraPost(
        "https://monokel.centra.com/api/checkout/vouchers",
        token,
        { voucher }
      );

      mutateSelection(data, false);
      setVoucherFormStatus("success");
      event.target.reset();
    } catch (error) {
      const status = error.message === "404" ? "notfound" : "error";
      setVoucherFormStatus(status);
    }
  }, []);

  const removeVoucher = useCallback(async (voucher) => {
    setIsAdjusting(true);

    const res = await fetch(
      `https://monokel.centra.com/api/checkout/vouchers/${voucher}`,
      {
        method: "DELETE",
        headers: {
          accept: `*/*; api-token=${token || "none"}`,
        },
      }
    );

    setIsAdjusting(false);
    const data = await res.json();
    mutateSelection(data, false);
  }, []);

  useEffect(() => {
    if (!voucherFormStatus) {
      return;
    }

    const timeout = setTimeout(() => {
      setVoucherFormStatus("");
    }, 10 * 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [voucherFormStatus]);

  let voucherFormResponse;

  if (voucherFormStatus === "notfound") {
    voucherFormResponse =
      "This code did not match any active gift card or discount. Was it entered correctly?";
  } else if (voucherFormStatus === "error") {
    voucherFormResponse =
      "There was an error applying your gift card. Please contact support.";
  }

  const updatePaymentFields = useCallback(
    debounce((newData) => {
      const paymentFields = {
        address: pickBy(newData.selection.address),
        shippingAddress: pickBy(newData.selection.shippingAddress),
        additionalNotes: newData.selection.additionalNotes,
      };

      if (!paymentFields.shippingAddress.country) {
        paymentFields.shippingAddress.country = data?.location.country;
      }

      window.CentraCheckout?.suspend();

      centraPut(
        "https://monokel.centra.com/api/checkout/payment-fields",
        token,
        paymentFields
      ).then((fetchedData) => {
        mutateSelection(fetchedData, false);
        window.CentraCheckout?.resume();
      });
    }, 2000),
    [token]
  );

  const setSelectionValue = useCallback(
    (name: string, value: string | boolean) => {
      const newData = JSON.parse(JSON.stringify(data));
      const keys = name.split(".");

      keys.reduce((result, key, i) => {
        if (i === keys.length - 1) {
          result[key] = value;
          return;
        }

        return result[key];
      }, newData);

      mutateSelection(newData, false);
      updatePaymentFields(newData);
    },
    [token, data]
  );

  useEffect(() => {
    if (ingridRef.current) {
      if (data?.selection.pluginFields?.shipwallet?.snippet) {
        ingridRef.current.innerHTML =
          data.selection.pluginFields.shipwallet.snippet;
        enlivenScripts(ingridRef.current);

        const reInitIngrid = once(() => {
          window.CentraCheckout?.reInitiate("shipwallet");
        });

        window._ingrid_initializeWidgetInstance = reInitIngrid;
        setTimeout(reInitIngrid, 750);
      } else {
        ingridRef.current.innerHTML = "";
        mutateSelection();
      }
    }
  }, [checkoutStep, Boolean(data?.selection.pluginFields?.shipwallet)]);

  useEffect(() => {
    if (adyenRef.current) {
      enlivenScripts(adyenRef.current);
    }
  }, [checkoutStep, adyenHtml]);

  useEffect(() => {
    function centraCheckoutCallback(event) {
      centraPut(
        "https://monokel.centra.com/api/checkout/payment-fields",
        token,
        event.detail
      ).then((fetchedData) => {
        mutateSelection(fetchedData, false);
      });
    }

    document.addEventListener(
      "centra_checkout_callback",
      centraCheckoutCallback
    );

    return () => {
      document.removeEventListener(
        "centra_checkout_callback",
        centraCheckoutCallback
      );
    };
  });

  useEffect(() => {
    function centraPaymentCallback(event) {
      const receiptUrl = new URL("/checkout/receipt", location.href);
      const failedUrl = new URL("/checkout/failed", location.href);

      centraPost("https://monokel.centra.com/api/checkout/payment", token, {
        address: event.detail.billingAddress,
        shippingAddress: event.detail.shippingAddress,
        paymentMethod: event.detail.paymentMethod,
        paymentMethodSpecificFields: event.detail.paymentMethodSpecificFields,
        paymentReturnPage: receiptUrl.href,
        paymentFailedPage: failedUrl.href,
        termsAndConditions,
      })
        .then((paymentData) => {
          if (paymentData.action === "success") {
            location.href = receiptUrl.href;
          } else if (paymentData.action === "redirect") {
            location.href = paymentData.url;
          } else if (paymentData.action === "form") {
            const form = document.createElement("form");
            form.method = "POST";
            form.action = paymentData.formUrl;

            for (const key in paymentData.formFields) {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = key;
              input.value = paymentData.formFields[key] || "";
              form.appendChild(input);
            }

            document.body.appendChild(form);
            form.submit();
          } else if (paymentData.action === "javascript") {
            const updateEvent = new CustomEvent(
              "centra_checkout_payment_response",
              { detail: paymentData.formFields }
            );

            // document.addEventListener(
            //   "centra_checkout_payment_response",
            //   (event) => {
            //     console.log(event);
            //   }
            // );
            document.dispatchEvent(updateEvent);
          }
        })
        .catch((e) => {
          const errorJSON = JSON.parse(e.message);
          const newURL = new URL(failedUrl.href);
          const query = new URLSearchParams();
          query.set("error", errorJSON.errors?.details);
          query.set("timestamp", new Date().getTime().toString());
          newURL.search = query.toString();
          location.href = newURL.toString();
        });
    }

    document.addEventListener(
      "centra_checkout_payment_callback",
      centraPaymentCallback
    );

    return () => {
      document.removeEventListener(
        "centra_checkout_payment_callback",
        centraPaymentCallback
      );
    };
  });

  useEffect(() => {
    if (!data || checkoutStep !== "payment") {
      return;
    }

    const address = !differentBillingAddress
      ? data.selection.shippingAddress
      : data.selection.address;

    centraPost("https://monokel.centra.com/api/checkout/payment", token, {
      address: {
        ...address,
        newsletter: subscribeToNewsletter,
      },
      shippingAddress: data.selection.shippingAddress,
      paymentReturnPage: new URL("/checkout/receipt", location.href).href,
      paymentFailedPage: new URL("/checkout/failed", location.href).href,
      paymentMethod: "adyen-drop-in-live",
      paymentInitiateOnly: true,
      termsAndConditions,
    }).then((paymentData) => {
      setAdyenHtml(paymentData.formHtml);
    });
  }, [checkoutStep]);

  const selectedCountry = data?.countries.find(
    (c) => c.country === data.location.country
  );

  const baseAddressFields: AddressField[] = [
    { name: "email", label: "Email", width: "half" },
    { name: "phoneNumber", label: "Phone", width: "half" },
    { name: "firstName", label: "First name", width: "half" },
    { name: "lastName", label: "Last name", width: "half" },
    { name: "address1", label: "Address", width: "half" },
    { name: "address2", label: "C/O", width: "half" },
    { name: "zipCode", label: "Zip code", width: "half" },
    { name: "city", label: "City", width: "half" },
    { name: "state", label: "State", width: "half" },
    { name: "country", label: "Country", width: "half" },
  ];

  const country = data?.countries.find(
    (c) => c.country === data?.location.country
  );

  const shippingAddressFields = baseAddressFields
    .filter(
      (f) =>
        (f.name === "state"
          ? data?.paymentFields.shippingAddress[f.name].required
          : data?.paymentFields.shippingAddress[f.name].visible) &&
        f.name !== "country"
    )
    .map((f) => {
      const field: any = {
        ...f,
        name: `shippingAddress.${f.name}`,
        required: data?.paymentFields.shippingAddress[f.name].required,
        type: data?.paymentFields.shippingAddress[f.name].type,
        value:
          data?.selection.shippingAddress[f.name] || data?.loggedIn?.[f.name],
      };

      if (f.name === "state" && country?.states) {
        const selected = country.states.find((s) => s.state === field.value);
        field.selectedLabel = selected?.name || "Select";
      }

      return field;
    });

  const getBillingCountry = data?.countries.find(
    (c) => c.country === billingCountry
  );

  const billingAddressFields = baseAddressFields
    .filter((f) => f.name !== "state" || getBillingCountry?.states)
    .map((f) => {
      const field: any = {
        ...f,
        name: `address.${f.name}`,
        required: data?.paymentFields.address[f.name].required,
        type: data?.paymentFields.address[f.name].type,
        value: data?.selection.address[f.name],
      };

      if (f.name === "state" && getBillingCountry?.states) {
        const selected = getBillingCountry.states.find(
          (s) => s.state === field.value
        );
        field.selectedLabel = selected?.name || "Select";
      }

      if (f.name === "country") {
        const selectedCountry = data?.countries.find(
          (c) => c.country === field.value
        );
        field.selectedLabel = selectedCountry?.name || "Select";
      }

      return field;
    });

  const shippingState = shippingAddressFields.find((f) =>
    f.name.endsWith(".state")
  );
  const billingState = billingAddressFields.find((f) =>
    f.name.endsWith(".state")
  );
  const billingCountryField = billingAddressFields.find((f) =>
    f.name.endsWith(".state")
  );

  useEffect(() => {
    if (country?.states && shippingState && !shippingState.value) {
      setSelectionValue(
        `selection.${shippingState.name}`,
        country.states[0].state
      );
    }
  }, [country?.states, shippingState?.value]);

  useEffect(() => {
    if (country?.states && billingState && !billingState.value) {
      setSelectionValue(
        `selection.${billingState.name}`,
        country.states[0].state
      );
    }
  }, [country?.states, billingState?.value, billingCountryField?.value]);

  const requiredIngridKeys = ["zipCode"];
  const ingridFieldsAreFilledOut = requiredIngridKeys.every(
    (k) => data?.selection.shippingAddress[k]
  );

  const checkoutShippingStep = (
    <section className="Checkout-column">
      <div className="Checkout-header">
        <h2 className="Checkout-title">Shipping</h2>
      </div>

      <div className="Checkout-labels">
        <label
          className={classNames("Checkout-label Checkout-label--full", {
            "is-disabled": isAdjusting,
          })}
        >
          <span className="Checkout-labelText">
            Country <abbr title="Required">*</abbr>
          </span>

          <div className="Checkout-fauxSelect">
            {selectedCountry?.name} &darr;
          </div>

          <select
            className="Checkout-select"
            name="country"
            disabled={isAdjusting}
            onChange={(event) => setCustomerCountry(event.currentTarget.value)}
            value={data?.location.country}
          >
            {data?.countries.map((c) => (
              <option value={c.country} key={c.country}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="Checkout-ingrid" ref={ingridRef} />

      <div className="Checkout-footer">
        <button
          className="Checkout-nextButton"
          onClick={() => {
            const items = data?.selection.items.map((item) => {
              return {
                item_id: item.product.sku,
                item_name: item.product.name,
                affiliation: item.product.brandName,
                item_collection: item.product.collectionName,
                item_variant: item.product.variantName,
                quantity: item.quantity,
                currency: data.selection.currency,
                price: item.product.priceAsNumber,
              };
            });
            ga.event({
              action: "add_shipping_info",
              params: {
                "x-fb-event_id": "add_shipping_info",
                value: data.selection.totals.grandTotalPriceAsNumber,
                currency: data.selection.currency,
                coupon: data.selection.discounts.vouchers[0] || false,
                shipping_tier: data.selection.shippingMethodName,
                items: items,
              },
            });
            setCheckoutStep("address");
          }}
        >
          Continue to address
          <br />
          <br />
        </button>
      </div>
    </section>
  );

  const checkoutAddressStep = (
    <section className="Checkout-column">
      <div className="Checkout-header">
        <button
          className="Checkout-headerButton"
          onClick={() => setCheckoutStep("shipping")}
          type="button"
        >
          Shipping
        </button>

        <h2 className="Checkout-title">Address</h2>
      </div>

      <div className="Checkout-labels">
        {shippingAddressFields.map((f) => (
          <label
            className={classNames(
              "Checkout-label",
              `Checkout-label--${f.width}`,
              {
                "is-active": activeInput === f.name,
                "is-disabled": isAdjusting,
              }
            )}
            key={f.name}
          >
            <span className="Checkout-labelText">
              {f.label} {f.required ? <abbr title="Required">*</abbr> : null}
            </span>

            {f.name.endsWith(".state") && country?.states ? (
              <>
                <div className="Checkout-fauxSelect">
                  {f.selectedLabel} &darr;
                </div>

                <select
                  className="Checkout-select"
                  name={f.name}
                  required={f.required}
                  disabled={isAdjusting}
                  onChange={(event) =>
                    setSelectionValue(`selection.${f.name}`, event.target.value)
                  }
                  value={f.value}
                >
                  {country.states.map((s) => (
                    <option value={s.state} key={s.state}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <input
                className="Checkout-input"
                name={f.name}
                required={f.required}
                type="text"
                form="address-form"
                value={f.value}
                disabled={isAdjusting}
                onChange={(event) =>
                  setSelectionValue(`selection.${f.name}`, event.target.value)
                }
                onFocus={() => setActiveInput(f.name)}
                onBlur={() => setActiveInput("")}
              />
            )}
          </label>
        ))}

        <div className="Checkout-label">
          <span className="Checkout-labelText">Different billing address?</span>

          <div className="Checkout-fauxCheckbox">
            <input
              className="Checkout-checkbox"
              checked={differentBillingAddress}
              onChange={(event) =>
                setDifferentBillingAddress(event.target.checked)
              }
              type="checkbox"
            />
            <span className="Checkout-fauxCheckboxYes">Yes</span>
            <span className="Checkout-fauxCheckboxNo">No</span>
          </div>
        </div>
      </div>

      {differentBillingAddress ? (
        <>
          <div className="Checkout-header">
            <h2 className="Checkout-title">Billing address</h2>
          </div>

          <div className="Checkout-labels">
            {billingAddressFields.map((f) => {
              let fieldInput = (
                <input
                  className="Checkout-input"
                  name={f.name}
                  required={f.required}
                  type="text"
                  form="address-form"
                  value={f.value}
                  disabled={isAdjusting}
                  onChange={(event) =>
                    setSelectionValue(`selection.${f.name}`, event.target.value)
                  }
                  onFocus={() => setActiveInput(f.name)}
                  onBlur={() => setActiveInput("")}
                />
              );

              if (f.name.endsWith(".state") && getBillingCountry?.states) {
                fieldInput = (
                  <>
                    <div className="Checkout-fauxSelect">
                      {f.selectedLabel} &darr;
                    </div>

                    <select
                      className="Checkout-select"
                      name={f.name}
                      required={f.required}
                      disabled={isAdjusting}
                      onChange={(event) =>
                        setSelectionValue(
                          `selection.${f.name}`,
                          event.target.value
                        )
                      }
                      value={f.value}
                    >
                      {getBillingCountry.states.map((s) => (
                        <option value={s.state} key={s.state}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </>
                );
              } else if (f.name.endsWith(".country")) {
                fieldInput = (
                  <>
                    <div className="Checkout-fauxSelect">
                      {f?.selectedLabel} &darr;
                    </div>

                    <select
                      className="Checkout-select"
                      name="country"
                      disabled={isAdjusting}
                      onChange={(event) => {
                        setSelectionValue(
                          `selection.${f.name}`,
                          event.target.value
                        );
                        setBillingCountry(event.currentTarget.value);
                        changeBillingCountry(event.currentTarget.value);
                      }}
                      value={f.value}
                    >
                      {data?.countries.map((c) => (
                        <option value={c.country} key={c.country}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </>
                );
              }

              return (
                <label
                  className={classNames(
                    "Checkout-label",
                    `Checkout-label--${f.width}`,
                    { "is-active": activeInput === f.name }
                  )}
                  key={f.name}
                >
                  <span className="Checkout-labelText">
                    {f.label}{" "}
                    {f.required ? <abbr title="Required">*</abbr> : null}
                  </span>

                  {fieldInput}
                </label>
              );
            })}
          </div>
          <div className="Checkout-labelText--notice">
            Please notice, that if the billing address is different from the
            shipping address, Klarna Payment methods may not work. Please use
            another payment method.
          </div>
        </>
      ) : null}

      <div className="Checkout-footer">
        <label className="Checkout-label">
          <span className="Checkout-labelText">
            Accept{" "}
            <Link href="/terms" target="_blank">
              Terms &amp; conditions
            </Link>{" "}
            <abbr title="Required">*</abbr>
          </span>

          <div className="Checkout-fauxCheckbox--single">
            <input
              className="Checkout-checkbox"
              checked={termsAndConditions}
              onChange={(event) => setTermsAndConditions(event.target.checked)}
              form="address-form"
              required
              type="checkbox"
            />
            <span className="Checkout-fauxCheckbox--singleBox"></span>
            <p>Yes</p>
          </div>
        </label>

        <div className="Checkout-label">
          <span className="Checkout-labelText">Subscribe to newsletter</span>

          <div className="Checkout-fauxCheckbox">
            <input
              className="Checkout-checkbox"
              checked={subscribeToNewsletter}
              form="address-form"
              onChange={(e) => {
                setSubscribeToNewsletter(e.target.checked);
              }}
              type="checkbox"
            />
            <span className="Checkout-fauxCheckboxYes">Yes</span>
            <span className="Checkout-fauxCheckboxNo">No</span>
          </div>
        </div>

        {/* <button
          className="Checkout-nextButton"
          form="address-form"
          onClick={() => {
            const items = data?.selection.items.map((item) => {
              return {
                item_id: item.product.sku,
                item_name: item.product.name,
                affiliation: item.product.brandName,
                item_collection: item.product.collectionName,
                item_variant: item.product.variantName,
                currency: data.selection.currency,
                quantity: item.quantity,
                price: item.product.priceAsNumber,
              };
            });
            ga.event({
              action: "add_payment_info",
              params: {
                "x-fb-event_id": "add_payment_info",
                value: data.selection.totals.grandTotalPriceAsNumber,
                currency: data.selection.currency,
                coupon: data.selection.discounts.vouchers[0] || false,
                shipping_tier: data.selection.shippingMethodName,
                items: items,
              },
            });
          }}
        >
          Continue to payment
          <br />
          <br />
        </button> */}

        <button
          className="Checkout-nextButton"
          form="address-form"
          onClick={() => {}}
        >
          Confirm details
          <br />
          <br />
        </button>
      </div>

      <form
        id="address-form"
        onSubmit={(e) => {
          if (showConfirmDetailsModal) {
            setCheckoutStep("payment");
          } else {
            e.preventDefault();
            setShowConfirmDetailsModal(true);
            setCurrentAdressDetails({
              shippingAddress: data?.selection.shippingAddress,
              shippingCountry: data?.countries.find((country) => {
                if (
                  country.country === data?.selection.shippingAddress.country
                ) {
                  return true;
                }
              }),
              billingAddress: data?.selection.address,
              billingCountry: data?.countries.find((country) => {
                if (country.country === data?.selection.address.country) {
                  return true;
                }
              }),
            });
          }
        }}
      />

      {showConfirmDetailsModal && (
        <button
          onClick={() => {
            setShowConfirmDetailsModal(false);
          }}
          className="Checkout-overlay"
        ></button>
      )}

      {/* Details modal */}
      <div
        className={`Checkout-confirmModal ${
          showConfirmDetailsModal ? "--animate" : ""
        }`}
      >
        <div className="Checkout-summaryInformationWrapper">
          <div className="Checkout-summaryBox">
            <p className="Checkout-summaryHeader">Please confirm your order</p>
          </div>
          <div className="Checkout-summaryBox Checkout-summaryBox--items">
            <p className="Checkout-summaryHeader">Items</p>

            {data?.selection?.items?.map((item) => {
              return (
                <div className="Checkout-summaryItemWrapper">
                  <p>{item.product.name}</p>
                  <p>{item.totalPrice}</p>
                </div>
              );
            })}
            <p className="Checkout-summaryHeader Checkout-summaryHeader--items">
              Total
            </p>
            <p>{data?.selection.totals.grandTotalPrice}</p>
          </div>
          <div className="Checkout-summaryBox">
            <p className="Checkout-summaryHeader">Shipping</p>
            <div className="Checkout-summaryListsWrapper">
              <ul>
                <li>
                  <p className="Checkout-summaryHeader">Address</p>
                </li>
                <li>
                  <span>
                    {currentAdressDetails?.shippingAddress["firstName"]}{" "}
                    {currentAdressDetails?.shippingAddress["lastName"]}
                  </span>
                </li>
                <li>
                  <span>
                    {currentAdressDetails?.shippingAddress["address1"]}
                  </span>
                </li>
                <li>
                  <span>
                    {currentAdressDetails?.shippingAddress["address2"] &&
                      "c/o "}
                    {currentAdressDetails?.shippingAddress["address2"]}
                  </span>
                </li>
                <li>
                  <span>
                    {currentAdressDetails?.shippingAddress["zipCode"]}
                  </span>
                </li>
                <li>
                  <span>{currentAdressDetails?.shippingAddress["city"]}</span>
                </li>
                {currentAdressDetails?.shippingAddress["state"].length > 0 && (
                  <li>
                    <span>
                      {currentAdressDetails?.shippingAddress["state"]}
                    </span>
                  </li>
                )}
                {currentAdressDetails?.shippingCountry &&
                  currentAdressDetails?.shippingCountry["name"].length > 0 && (
                    <li>
                      <span>
                        {currentAdressDetails?.shippingCountry["name"]}
                      </span>
                    </li>
                  )}
              </ul>
              <ul>
                <li>
                  <p className="Checkout-summaryHeader">Email</p>
                </li>
                <li>
                  <span>{currentAdressDetails?.shippingAddress["email"]}</span>
                </li>
                <br />
                <p className="Checkout-summaryHeader">Phone</p>
                <li>
                  <span>
                    {currentAdressDetails?.shippingAddress["phoneNumber"]}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="Checkout-summaryBox">
            {differentBillingAddress && (
              <>
                <p className="Checkout-summaryHeader">Billing</p>
                <div className="Checkout-summaryListsWrapper">
                  <ul>
                    <li>
                      <p className="Checkout-summaryHeader">Address</p>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["firstName"]}{" "}
                        {currentAdressDetails?.billingAddress["lastName"]}
                      </span>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["address1"]}
                      </span>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["address2"] &&
                          "c/o "}
                        {currentAdressDetails?.billingAddress["address2"]}
                      </span>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["zipCode"]}
                      </span>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["city"]}
                      </span>
                    </li>
                    {currentAdressDetails?.billingAddress["state"].length >
                      0 && (
                      <li>
                        <span>
                          {currentAdressDetails?.billingAddress["state"]}
                        </span>
                      </li>
                    )}
                    {currentAdressDetails?.billingCountry &&
                      currentAdressDetails?.billingCountry["name"].length >
                        0 && (
                        <li>
                          <span>
                            {currentAdressDetails?.billingCountry["name"]}
                          </span>
                        </li>
                      )}
                  </ul>
                  <ul>
                    <li>
                      <p className="Checkout-summaryHeader">Email</p>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["email"]}
                      </span>
                    </li>
                    <br />
                    <li>
                      <p className="Checkout-summaryHeader">Phone</p>
                    </li>
                    <li>
                      <span>
                        {currentAdressDetails?.billingAddress["phoneNumber"]}
                      </span>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="Checkout-navigationButtonsWrapper">
          <button
            className="Checkout-returnLink"
            onClick={() => {
              setShowConfirmDetailsModal(false);
            }}
          >
            Edit details
            <br />
            <br />
          </button>
          <button
            className="Checkout-nextButton"
            form="address-form"
            onClick={() => {
              const items = data?.selection.items.map((item) => {
                return {
                  item_id: item.product.sku,
                  item_name: item.product.name,
                  affiliation: item.product.brandName,
                  item_collection: item.product.collectionName,
                  item_variant: item.product.variantName,
                  currency: data.selection.currency,
                  quantity: item.quantity,
                  price: item.product.priceAsNumber,
                  original_price: item.product.priceBeforeDiscountAsNumber,
                  price_ex_vat: item.product.priceEachWithoutTaxAsNumber,
                };
              });
              ga.event({
                action: "add_payment_info",
                params: {
                  "x-fb-event_id": "add_payment_info",
                  transaction_id: data.selection.order,
                  value: data.selection.totals.grandTotalPriceAsNumber,
                  currency: data.selection.currency,
                  tax: data.selection.grandTotalPriceTaxAsNumber,
                  shipping: data.selection.totals.shippingPriceAsNumber,
                  subtotal:
                    data.selection.totals.grandTotalPriceAsNumber -
                    data.selection.totals.grandTotalPriceTaxAsNumber -
                    data.selection.totals.shippingPriceAsNumber,
                  coupon: data.selection.discounts.vouchers[0] || false,
                  discount:
                    data.selection.totals.totalDiscountPriceAsNumber || 0,
                  shipping_tier: data.selection.shippingMethodName,
                  items: items,
                },
              });
            }}
          >
            Continue to payment
            <br />
            <br />
          </button>
        </div>
      </div>
    </section>
  );

  const checkoutPaymentStep = (
    <section className="Checkout-column">
      <div className="Checkout-header">
        <button
          className="Checkout-headerButton"
          onClick={() => setCheckoutStep("shipping")}
          type="button"
        >
          Shipping
        </button>

        <button
          className="Checkout-headerButton"
          onClick={() => setCheckoutStep("address")}
          type="button"
        >
          Address
        </button>

        <h2 className="Checkout-title">Payment</h2>
      </div>

      <div
        className="Checkout-adyen"
        ref={adyenRef}
        dangerouslySetInnerHTML={{ __html: adyenHtml }}
      />

      <div className="Checkout-footer"></div>
    </section>
  );

  const checkout = (
    <div className="Checkout">
      <section className="Checkout-column">
        <div className="Checkout-header">
          <h2 className="Checkout-title">Bag</h2>
        </div>

        <ul className="Cart-items Cart-items--checkout">
          {data?.selection.items.map((item) => (
            <li
              className={classNames("Cart-item", {
                "is-disabled": isAdjusting,
              })}
              key={item.line}
            >
              {item.product.media["1000x0"]?.[0] ? (
                <Link className="Cart-itemImage" href={"/" + item.product.uri}>
                  <CentraProductImage
                    src={item.product.media["1000x0"][0]}
                    sizes="(min-width: 1024px) 25vw, 50vw"
                  />
                </Link>
              ) : null}

              <div className="Cart-itemText">
                <h4 className="Cart-itemTextRow">
                  <Link href={"/" + item.product.uri}>{item.product.name}</Link>
                  , {item.priceEach}
                </h4>

                <div className="Cart-itemTextRow">
                  <span>Quantity</span>

                  <div className="Cart-itemQtyButtons">
                    {times(10, (i) => (
                      <button
                        className={classNames("Cart-itemQtyButton", {
                          "is-active": item.quantity === i + 1,
                        })}
                        key={i}
                        onClick={() => setItemQuantity(item, i + 1)}
                      >
                        {leftPad(i + 1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="Cart-itemTextRow">
                  <button
                    className="Cart-itemRemove"
                    onClick={() => {
                      ga.event({
                        action: "remove_from_cart",
                        params: {
                          "x-fb-event_id": "remove_from_cart",
                          items: [
                            {
                              item_id: item.sku,
                              item_name: item.name,
                              affiliation: item.brandName,
                              item_collection: item.collectionName,
                              item_variant: item.variantName,
                              currency: data.selection.currency,
                              quantity: item.quantity,
                              price: item.priceAsNumber,
                            },
                          ],
                        },
                      });
                      setItemQuantity(item, 0);
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div className="Cart-itemTextRow Cart-itemTextRow--bottom">
                  Subtotal {item.totalPrice}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="Checkout-labels">
          {!showVoucherForm ? (
            <button
              className="Checkout-label Checkout-label--full"
              onClick={() => setShowVoucherForm(true)}
              type="button"
            >
              <div className="Checkout-labelText">Click to add a voucher</div>
              <div className="Checkout-labelValue">&nbsp;</div>
            </button>
          ) : null}

          {showVoucherForm ? (
            <label
              className={classNames("Checkout-label Checkout-label--full", {
                "is-active": activeInput === "voucher",
              })}
            >
              <span className="Checkout-labelText">Voucher code</span>

              <input
                autoFocus
                className="Checkout-input"
                name="voucher"
                required
                type="text"
                form="voucher-form"
                disabled={voucherFormStatus === "submitting"}
                onFocus={() => setActiveInput("voucher")}
                onBlur={() => setActiveInput("")}
              />

              <button className="Checkout-inlineButton" form="voucher-form">
                {voucherFormStatus === "submitting" ? "Submitting" : "Submit"}
              </button>
            </label>
          ) : null}

          {voucherFormResponse ? (
            <div className="Checkout-label Checkout-label--full">
              <div className="Checkout-labelValue">{voucherFormResponse}</div>
            </div>
          ) : null}

          {data?.selection.discounts.vouchers.map((v) => (
            <div
              className="Checkout-label Checkout-label--full"
              key={v.voucher}
            >
              <div className="Checkout-labelText">Voucher ”{v.voucher}”</div>
              <div className="Checkout-labelValue">{v.priceOff}</div>

              <button
                className="Checkout-inlineButton"
                disabled={isAdjusting}
                onClick={() => removeVoucher(v.voucher)}
                type="button"
              >
                Remove
              </button>
            </div>
          ))}

          <div className="Checkout-label Checkout-label--full">
            <div className="Checkout-labelText">Shipping</div>
            {ingridFieldsAreFilledOut ? (
              <div className="Checkout-labelValue">
                {data?.selection.totals.shippingPriceAsNumber
                  ? data?.selection.totals.shippingPrice
                  : "Free"}
              </div>
            ) : (
              <div className="Checkout-labelValue">
                {" "}
                {data?.selection.totals.shippingPriceAsNumber
                  ? data?.selection.totals.shippingPrice
                  : "Free"}
              </div>
            )}
          </div>

          {data?.selection.totals.taxPercent &&
          !data?.selection.totals.taxDeducted ? (
            <div className="Checkout-label Checkout-label--full">
              <div>
                <div className="Checkout-labelText">VAT</div>
                <div className="Checkout-labelValue">
                  {numberWithSpaces(
                    data?.selection.totals.grandTotalPriceAsNumber *
                      (data?.selection.totals.taxPercent / 100)
                  )}{" "}
                  {data?.selection.currency}
                </div>
              </div>
            </div>
          ) : null}

          {data?.selection.totals.taxDeducted && (
            <div className="Checkout-label Checkout-label--full">
              <div>
                <div className="Checkout-labelText">VAT</div>
                <div className="Checkout-labelValue">
                  {data?.selection.totals.taxDeducted}
                </div>
              </div>
            </div>
          )}

          <div className="Checkout-label Checkout-label--full">
            <div className="Checkout-labelText">Total</div>
            <div className="Checkout-labelValue--row">
              {data?.selection.totals.grandTotalPrice}{" "}
              <span className="Checkout-labelValue--light">
                &nbsp;
                {data?.selection.totals.taxPercent &&
                !data?.selection.totals.taxDeducted
                  ? `(incl. ${isEU ? "VAT" : "tax"})`
                  : `(excl. ${isEU ? "VAT" : "tax"})`}
              </span>
            </div>
          </div>

          {!data?.selection.totals.taxPercent &&
          !data?.selection.totals.taxDeducted ? (
            <div className="Checkout-labelText--full">
              {" "}
              Note that your order is shipped DDU (delivered duty unpaid) and
              might be subject to import Tax and duty.
            </div>
          ) : null}

          {data?.selection.totals.taxDeducted && (
            <div className="Checkout-labelText--full">
              {" "}
              Note that your order is sold excl. VAT and shipped DDU (delivered
              duty unpaid) and might be subject to import Tax and duty.
            </div>
          )}
        </div>

        <form
          id="voucher-form"
          onSubmit={(event) => {
            event.preventDefault();
            submitApplyVoucher(event);
          }}
        />

        <div className="Checkout-footer">
          <Link className="Checkout-returnLink" href="/store">
            Return to store
            <br />
            <br />
          </Link>
        </div>
      </section>

      {checkoutStep === "shipping" ? checkoutShippingStep : null}
      {checkoutStep === "address" ? checkoutAddressStep : null}
      {checkoutStep === "payment" ? checkoutPaymentStep : null}
    </div>
  );

  return (
    <StoryProvider {...props}>
      <NextSeo
        title="Checkout – Monokel Eyewear"
        additionalMetaTags={[
          {
            name: "robots",
            content: "noindex",
          },
        ]}
      />

      <Header isCheckout />

      {!data?.selection.items.length ? (
        <div className="Checkout">
          <section className="Checkout-column">
            <div className="Checkout-header">
              <h2 className="Checkout-title">Bag</h2>
            </div>

            <div className="Checkout-labels">
              <div className="Checkout-label Checkout-label--full">
                <div className="Checkout-labelValue">No items in your bag</div>
                <div className="Checkout-labelValue">&nbsp;</div>
              </div>
            </div>

            <div className="Checkout-footer">
              <Link className="Checkout-returnLink" href="/store">
                Return to store
                <br />
                <br />
              </Link>
            </div>
          </section>
        </div>
      ) : (
        checkout
      )}

      {data?.selection.centraCheckoutScript ? (
        <Script
          id="centra-checkout"
          dangerouslySetInnerHTML={{
            __html: data?.selection.centraCheckoutScript,
          }}
        />
      ) : null}

      <Footer />
    </StoryProvider>
  );
}

export { getStaticProps };
